import { useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import { keyBy, keys } from 'lodash';
import {
  Flex,
  Box,
  Input,
  InputGroup,
  InputRightAddon,
  Switch,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import { StylesConfig } from 'react-select/dist/declarations/src/styles';
import { momentTimezone } from '../utils/dateUtils';
import { getReadableColor, formatDate, formatPhoneNumber } from '../../helpers';
import { CirlceIcon, allIcons } from '../utils/Icon';
import AddressAutocomplete from './AddressAutocomplete';
import { TField } from './Form';
import FormGroup from './FormGroup';
import RCheckbox from './RCheckbox';
import RSelect from './RSelect';
import SelectInput from './SelectInput';
import TextInput from './TextInput';
import TreeInput from './TreeInput';
import { useCanI, useGetSelect } from '../../redux/actions';
import { TState } from '../../redux/store';
import TripleSwitch from '../common/TripleSwitch';
import RSiret from '../common/Siret';
import RepeatGroup from './RepeatGroup';
import Counter from './Counter';

const inputStyle = {
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: getReadableColor(data.color),
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: data.color,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: getReadableColor(data.color),
    ':hover': {
      opacity: 0.5,
    },
  }),
} as StylesConfig<any, any, any>;

export const useCanUpdateInput = () => {
  const canI = useCanI();

  return (role: string) =>
    canI(role, 'update') ||
    canI(`${role}_street`, 'update') ||
    canI(`${role}_city`, 'update') ||
    canI(`${role}_postal`, 'update') ||
    canI(`${role}_dept`, 'update');
};

export const RInput = (props: any) => {
  const {
    f,
    formOptions,
    formData,
    handleOnChange,
    handleReset,
    setFormData,
    setPending,
    isError,
    onChange,
  } = props;

  const modalType = useSelector((state: TState) => state.modalType);
  const rowData = useSelector((state: TState) => state.rowData);
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);

  const getSelectByName = useGetSelect();
  const canUpdateInput = useCanUpdateInput();

  const { direction, planable } = formOptions || {};

  const name = (!f.baseName && f.name_id) || f.name || f._id || '';

  const selectInput = f.name_id
    ? getSelectByName(f.name_id, formData[name])
    : null;

  const field: TField = {
    ...f,
    options: f.optionsFn
      ? f.optionsFn(formData)
      : selectInput?.options || f.options,
  };

  const allOptions = selectInput?.allOptions || field.options;

  const roleName = f.baseName || f.name_id || f.name || f._id || '';

  const isDisabled =
    field.disabled ||
    (/editEntry/.test(modalType) && !canUpdateInput(roleName));

  const groupOptions = keys(keyBy(dataInputs, 'group'))
    .filter((f) => f && f !== 'undefined')
    .map((v) => ({
      label: v,
      value: v,
    }));

  const { isMulti, placeholder, typeFn, type } = field;
  const fieldType = typeFn?.(formData) || type;

  switch (fieldType) {
    case 'users':
      const userOptions: TField['options'] =
        field.options ||
        userList.map((u) => ({
          label: u.name || u.login,
          value: u._id,
          color: u.userColor,
        })) ||
        [];

      const userCurrOption =
        formData[name] == null
          ? null
          : isMulti
            ? userOptions.filter((option: any) =>
                formData[name].includes(option.value),
              )
            : userOptions.find(
                (option: any) => option.value === formData[name],
              );

      return (
        <Flex alignItems='center'>
          <RSelect
            isDisabled={isDisabled}
            closeMenuOnSelect={false}
            onChange={(e: any) =>
              handleOnChange(
                name,
                isMulti ? e.map((opt: any) => opt.value) : e?.value,
              )
            }
            value={userCurrOption}
            options={userOptions.filter(
              (opt: any) => opt.value && !opt.disabled,
            )}
            placeholder={placeholder || ''}
            isMulti={isMulti}
            styles={inputStyle}
          />
        </Flex>
      );
    case 'select':
      const currOption =
        formData[name] == null
          ? null
          : allOptions?.find((opt) => `${opt.value}` === `${formData[name]}`);

      const hasEmptySwitch = field.withEmpty;

      return (
        <Flex alignItems='center'>
          <RSelect
            isDisabled={isDisabled}
            closeMenuOnSelect={false}
            onChange={(e: any) =>
              handleOnChange(
                name,
                isMulti
                  ? e.map((opt: any) => opt.value)
                  : hasEmptySwitch && e?.value == null
                    ? undefined
                    : e?.value,
                hasEmptySwitch,
              )
            }
            value={currOption}
            options={(field.options || [])?.filter((o) => !o.disabled)}
            placeholder={placeholder || ''}
            isMulti={isMulti}
            styles={inputStyle}
          />
          {hasEmptySwitch && (
            <Flex ml={3} pb={1} alignItems='center' gap={2}>
              Vider
              <Switch
                isChecked={formData[name] === null}
                onChange={(e) =>
                  handleOnChange(
                    name,
                    e.target.checked ? null : undefined,
                    true,
                  )
                }
              />
            </Flex>
          )}
        </Flex>
      );
    case 'multiselect':
      try {
        const multiValue = formData[name]?.map((data: any) =>
          allOptions?.find((fo) => fo.value === data),
        );

        const hasEmptySwitch = field.withEmpty;
        const max = field.max || 100;

        return (
          <Flex alignItems='center'>
            <RSelect
              closeMenuOnSelect={false}
              isDisabled={isDisabled}
              isMulti
              onChange={(e) => {
                handleOnChange(
                  name,
                  hasEmptySwitch && !e.length
                    ? undefined
                    : e.map((multiVal: any) => multiVal.value),
                  hasEmptySwitch,
                );
              }}
              value={multiValue || []}
              options={field.options?.filter((o) => !o.disabled)}
              placeholder={f.placeholder || ''}
              styles={{
                multiValueLabel: (styles, { data }) => ({
                  ...styles,
                  color: getReadableColor(data.color),
                }),
                multiValue: (styles, { data }) => ({
                  ...styles,
                  backgroundColor: data.color,
                }),
                multiValueRemove: (styles, { data }) => ({
                  ...styles,
                  color: getReadableColor(data.color),
                  ':hover': {
                    opacity: 0.5,
                  },
                }),
              }}
              max={max}
            />
            {hasEmptySwitch && (
              <Flex ml={3} pb={1} alignItems='center' gap={2}>
                Vider
                <Switch
                  isChecked={formData[name] === null}
                  onChange={(e) =>
                    handleOnChange(
                      name,
                      e.target.checked ? null : undefined,
                      true,
                    )
                  }
                />
              </Flex>
            )}
          </Flex>
        );
      } catch (e) {
        return <></>;
      }
    case 'address':
      const addr = {
        street: formData[`${name}_street`],
        city: formData[`${name}_city`],
        postal: formData[`${name}_postal`],
        id: formData[name],
      };

      const gap = [0, 0, '8px', '16px'];

      return (
        <Flex w='100%' flexWrap='wrap' gap={gap}>
          <Box
            w={
              field.noFormat
                ? ['100%', '100%', '100%', 'calc(33.33% - 10px)']
                : '100%'
            }
          >
            <FormGroup
              field={field}
              prefixLabel='adresse'
              planable={planable}
              direction={direction}
              isError={isError}
              input={
                <AddressAutocomplete
                  onChange={onChange}
                  isDisabled={isDisabled}
                  setFormData={setFormData}
                  addressValue={addr}
                  label={name}
                  setPending={setPending}
                  type='street'
                />
              }
            />
          </Box>
          <Flex
            w={field.noFormat ? 'auto' : '100%'}
            alignItems='end'
            flexWrap='wrap'
            flexGrow={1}
            gap={gap}
          >
            <Box w={['100%', '100%', '100%', 'calc(50% - 10px)']}>
              <FormGroup
                field={field}
                prefixLabel='ville'
                direction={direction}
                isError={isError}
                input={
                  <AddressAutocomplete
                    onChange={onChange}
                    isDisabled={isDisabled}
                    setFormData={setFormData}
                    addressValue={addr}
                    label={name}
                    setPending={setPending}
                    type='city'
                  />
                }
              />
            </Box>
            <Box flexGrow={1}>
              <FormGroup
                field={field}
                prefixLabel='code postal'
                direction={direction}
                isError={isError}
                input={
                  <AddressAutocomplete
                    onChange={onChange}
                    isDisabled={isDisabled}
                    setFormData={setFormData}
                    addressValue={addr}
                    label={name}
                    setPending={setPending}
                    type='postal'
                  />
                }
              />
            </Box>
            <Flex gap={1}>
              {!addr.id && (
                <Tooltip label='Adresse non valide'>
                  <span>
                    <CirlceIcon
                      className='xmark'
                      as={allIcons.HiMiniExclamationTriangle}
                      color='orange.400'
                      mb={0.5}
                    />
                  </span>
                </Tooltip>
              )}
              {!isDisabled && (
                <CirlceIcon
                  className='xmark'
                  as={allIcons.HiMiniXMark}
                  onClick={() => handleReset(name)}
                  mb={0.5}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      );
    case 'tree':
      return (
        <TreeInput
          list={field.list || []}
          actions={field.actions || []}
          selected={field.selected}
          onChange={(e: any) => handleOnChange(name, e)}
        />
      );
    case 'selectInput':
      return (
        <SelectInput
          orderedOptions={formData.orderedOptions}
          setFormData={(values: any) => {
            handleOnChange('options', values);
            handleOnChange('orderedOptions', values);
          }}
        />
      );
    case 'multiSelect':
      return (
        <RSelect
          isDisabled={isDisabled}
          closeMenuOnSelect={false}
          defaultValue={field.options?.filter((option) =>
            rowData.options?.includes(option.value),
          )}
          isMulti
          onChange={(opts) => {
            handleOnChange(
              'options',
              opts.map((opt: any) => opt.value),
            );
          }}
          options={field.options}
        />
      );
    case 'checkbox':
      return (
        <Flex h='32px' alignItems='center'>
          <RCheckbox
            isDisabled={isDisabled}
            width='24px'
            height='24px'
            isChecked={!!formData[name]}
            onChange={(val: any) => handleOnChange(name, val)}
          />
        </Flex>
      );
    case 'tripleChoice':
      return (
        <TripleSwitch
          value={formData[name]}
          onChange={(val: boolean | undefined) =>
            handleOnChange(name, val, true)
          }
        />
      );
    case 'password':
    case 'text':
      return (
        <Input
          isDisabled={isDisabled}
          pl={2}
          h='32px'
          type={fieldType}
          onChange={(e: any) => handleOnChange(name, e?.target?.value)}
          value={formData[name] ?? ''}
          bgColor={'#ffffff'}
          autoComplete='off'
        />
      );
    case 'siret':
      return <RSiret {...props} f={f} />;
    case 'date':
      return (
        <Input
          isDisabled={isDisabled}
          pl={2}
          h='32px'
          type={field.showTime ? 'datetime-local' : fieldType}
          onChange={(e: any) =>
            handleOnChange(name, momentTimezone(e?.target?.value))
          }
          value={
            formatDate(
              formData[name],
              true,
              field.showTime ? 'YYYY-MM-DDTHH:mm' : undefined,
            ) ?? ''
          }
          bgColor={'#ffffff'}
        />
      );
    case 'createSelect':
      return (
        <>
          <CreatableSelect
            isClearable
            options={groupOptions}
            defaultValue={{
              label: formData[name] ?? '',
              value: formData[name] ?? '',
            }}
            formatCreateLabel={(text) => `Ajouter ${text}`}
            onChange={(e: any) => handleOnChange(name, e?.value)}
          />
          <Text fontStyle='italic' fontSize='.8em'>
            Pour créer un nouveau groupe, taper le nom puis <b>Ajouter</b>
          </Text>
        </>
      );
    case 'tel':
      return (
        <Input
          isDisabled={isDisabled}
          pl={2}
          minH='32px'
          type={fieldType}
          onChange={(e: any) => handleOnChange(name, e?.target?.value)}
          onBlur={(e: any) =>
            handleOnChange(name, formatPhoneNumber(e?.target?.value))
          }
          value={formData[name] ?? ''}
          bgColor={'#ffffff'}
        />
      );
    case 'number':
      return (
        <InputGroup>
          <Input
            isDisabled={isDisabled}
            minH='32px'
            min={0}
            type='number'
            onChange={(e: any) => handleOnChange(name, e?.target?.value)}
            value={formData[name] ?? ''}
            bgColor={'#ffffff'}
          />
          {field.unity && (
            <InputRightAddon h='32px'>{field.unity}</InputRightAddon>
          )}
        </InputGroup>
      );
    case 'switch':
      return (
        <Flex alignItems='center' minH='32px'>
          <Switch
            isDisabled={isDisabled}
            alignItems='center'
            isChecked={formData[name]}
            onChange={(e: any) => handleOnChange(name, e.target.checked)}
            size='lg'
          />
        </Flex>
      );
    case 'title':
      return null;
    case 'date_range':
    case 'number_range':
      const isDate = /date/.test(fieldType);

      const handleRangeChange = (e: any) =>
        !e?.target?.value
          ? undefined
          : isDate
            ? e?.target?.value
            : Number(e?.target?.value);

      return (
        <Flex gap={2}>
          <Input
            isDisabled={isDisabled}
            pl={2}
            h='32px'
            type={isDate ? 'date' : 'number'}
            onChange={(e: any) =>
              handleOnChange(name, [
                handleRangeChange(e),
                formData[name]?.[1] ||
                  (isDate && e?.target?.value ? e?.target?.value : undefined),
              ])
            }
            value={
              (isDate
                ? formatDate(formData[name]?.[0], true)
                : formData[name]?.[0]) ?? ''
            }
            placeholder='Min'
            bgColor={'#ffffff'}
          />
          <Input
            isDisabled={isDisabled}
            pl={2}
            h='32px'
            type={isDate ? 'date' : 'number'}
            onChange={(e: any) =>
              handleOnChange(name, [formData[name]?.[0], handleRangeChange(e)])
            }
            value={
              (isDate
                ? formatDate(formData[name]?.[1], true)
                : formData[name]?.[1]) ?? ''
            }
            placeholder='Max'
            bgColor={'#ffffff'}
          />
        </Flex>
      );
    case 'repeatgroup':
      return <RepeatGroup {...props} input={f} />;
    case 'counter':
      return (
        <Counter {...props} value={formData[name]} isDisabled={isDisabled} />
      );
    default:
      return (
        <TextInput
          name={name}
          isDisabled={isDisabled}
          value={formData[name]}
          handleOnChange={handleOnChange}
          bgColor={'#ffffff'}
        />
      );
  }
};

export default RInput;
